import { MDBCardBody, MDBCardHeader } from "mdb-react-ui-kit";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock, faHandshake, faLock, faMapMarker, faTrash } from "@fortawesome/free-solid-svg-icons";
import { useRecoilState } from "recoil";
import {
    ConfirmationModal,
    FormAutocomplete,
    Input,
    NewImageUpload,
    RowInfo,
    Textarea,
} from "../common";
import { Button, Row, Col, NpcItemActionButtonWrapper, Divider } from "../App.styles";
import { highlightIdState, useNpcItemsQuery, useQueryInvalidateInfinite, useQueryNoInvalidate } from "./Npc.state";
import { useGameQuery } from "../settings/Settings.state";
import usePreviewButton from "../hooks/usePreviewButton";
import useWhitelabelFlag from "../hooks/useWhitelabelFlag";
import { getWhitelabelFeatures, WHITELABEL_FLAG_KEYS } from "../consts/whitelabel";
import DescriptionModal from "./modals/DescriptionModal";
import TitleModal from "./modals/TitleModal";
import ImageModal from "./modals/ImageModal";
import AdvancedSettings from '../common/AdvancedSettings'
import { useDecision } from '@optimizely/react-sdk'
import { useMemo, useEffect } from 'react'
import LocksSection from './sections/LocksSection'
import LocationSection from './sections/LocationSection'
import TimersSection from './sections/TimersSection'
import InteractionsSection from './sections/InteractionsSection'
import BioModal from "./modals/BioModal";
import VoiceModal from "./modals/VoiceModal";

const voiceOptions = [
    { id: "", name: "" },
    { id: "alloy", name: "Alloy" },
    { id: "ash", name: "Ash" },
    { id: "ballad", name: "Ballad" },
    { id: "ceda", name: "Cedar" },
    { id: "coral", name: "Coral" },
    { id: "echo", name: "Echo" },
    { id: "marin", name: "Marin" },
    // { id: "fable", name: "Fable" },
    // { id: "onyx", name: "Onyx" },
    // { id: "nova", name: "Nova" },
    { id: "sage", name: "Sage" },
    { id: "shimmer", name: "Shimmer" },
    { id: "verse", name: "Verse" },
];

const NpcItemEdit = ({ id, onClose, onDelete: destroy, isDeleting }) => {
    const { t } = useTranslation()
    // Retrieve current NPC item data (no invalidation needed for simple read/update)
    const [npcItem, setNpcItem] = useNpcItemsQuery(id, { invalidate: false })
    const useQuery = (config) => useNpcItemsQuery(id, config)
    const useQueryNoInvalidate_ = () => useQueryNoInvalidate(id)
    const useQueryInvalidateInfinite_ = () => useQueryInvalidateInfinite(id)
    const [{ code }] = useGameQuery();
    const [, setHighlightId] = useRecoilState(highlightIdState)
    const onSave = async () => {
        await onClose()
        setHighlightId(id)
    }
    const previewBtnProps = usePreviewButton({ code, path: 'messaging' })
    const showAppPreview = useWhitelabelFlag(
        WHITELABEL_FLAG_KEYS.showAppPreview,
        getWhitelabelFeatures().showAppPreview,
    );
    const [decisionNpcLocks] = useDecision('npc-locks');
    const [decisionNpcLocation] = useDecision('npc-location');
    const [decisionNpcTimers] = useDecision('npc-timers');
    const [decisionNpcInteractions] = useDecision('npc-interactions');
    const shouldShowAdvancedSettings = useMemo(() => {
        return decisionNpcLocks?.enabled ||
            decisionNpcLocation?.enabled ||
            decisionNpcTimers?.enabled ||
            decisionNpcInteractions?.enabled
    }, [
        decisionNpcLocks,
        decisionNpcLocation,
        decisionNpcTimers,
        decisionNpcInteractions,
    ])

    // -------------------------------------------------------------------------
    // Ensure "voice" has a default value
    // -------------------------------------------------------------------------
    useEffect(() => {
        // If voice is empty or undefined, pick the first non-empty option
        if (npcItem && (!npcItem.voice || npcItem.voice.trim() === "")) {
            const defaultVoice = voiceOptions.find(({ id }) => id)
            if (defaultVoice) {
                // Persist update to local state and API
                setNpcItem({ voice: defaultVoice.id })
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [npcItem?.voice])

    const ActionDelete = () => (
        <ConfirmationModal
            title={t("Confirm Deletion")}
            body={t("Are you sure you wish to delete?")}
            confirmLabel={t("Delete")}
            confirmAction={() => {
                onClose && onClose()
                destroy(id)
            }}
            isLoading={isDeleting}
        >
            <Button disabled={isDeleting} className="btn--flat" color="warning">
                <FontAwesomeIcon icon={faTrash} /> {t('Delete')}
            </Button>
        </ConfirmationModal>
    )

    const settingsTabs = useMemo(() => {
        const tabs = []
        if (decisionNpcLocks?.enabled) {
            tabs.push({
                title: <><FontAwesomeIcon icon={faLock} /> {t('Locks')}</>, 
                content: <LocksSection useQuery={useQueryNoInvalidate_} />,
            })
        }
        if (decisionNpcLocation?.enabled) {
            tabs.push({
                title: <><FontAwesomeIcon icon={faMapMarker} /> {t('Location')}</>, 
                content: <LocationSection useQuery={useQueryNoInvalidate_} />,
            })
        }
        if (decisionNpcTimers?.enabled) {
            tabs.push({
                title: <><FontAwesomeIcon icon={faClock} /> {t('Timers')}</>, 
                content: <TimersSection useQuery={useQueryNoInvalidate_} />,
            })
        }
        if (decisionNpcInteractions?.enabled) {
            tabs.push({
                title: <><FontAwesomeIcon icon={faHandshake} /> {t('Interactions')}</>, 
                content: <InteractionsSection useQuery={useQueryNoInvalidate_} />,
            })
        }
        return tabs
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        decisionNpcLocks?.enabled,
        decisionNpcLocation?.enabled,
        decisionNpcTimers?.enabled,
        t,
    ])

    return (
        <>
            <MDBCardHeader>
                <Row>
                    <Col size="12" lg="auto" className="align-items-start mb-4 col--stretch col--grow">
                        <RowInfo help={<TitleModal />}>
                            <Input
                                data-testid="npc-item__input-title"
                                name="name"
                                label={t("NPC Name")}
                                useQuery={useQueryInvalidateInfinite_}
                                mode="clean"
                            />
                        </RowInfo>
                    </Col>
                </Row>
            </MDBCardHeader>
            <MDBCardBody>
                <Row>
                    <Col size="12" lg="auto" className="align-items-start col--stretch col--grow">
                        <RowInfo help={<ImageModal />}>
                            <NewImageUpload name="media" label={t("Image")} useQuery={useQuery} standalone withAi />
                        </RowInfo>
                    </Col>
                </Row>
                <div className="mt-4 mt-lg-0 mb-4">
                    <RowInfo help={<VoiceModal />}>
                        <Row className="flex-grow-1">
                            <Col size="9" className="col--stretch">
                                <FormAutocomplete
                                    desc={t("This is the voice you want the NPC to use when talking to the players.")}
                                    label={t("Voice")}
                                    name="voice"
                                    useQuery={useQueryNoInvalidate_}
                                    options={voiceOptions}
                                    size="small"
                                />
                            </Col>  
                            <Col size="3" className="align-items-start">
                                <Button
                                    href="https://www.openai.fm/"
                                    target="_blank"
                                    outline
                                >
                                    {t("Preview")}
                                </Button>
                            </Col>
                        </Row>
                    </RowInfo>
                </div>
                <div className="mt-4 mt-lg-0 mb-4">
                    <RowInfo help={<DescriptionModal />}>
                        <Textarea
                            help={t("This is the main text for your challenge.")}
                            label={t("Description")}
                            name="prompt"
                            useQuery={useQueryNoInvalidate_}
                            mode="clean"
                            rows="5"
                        />
                    </RowInfo>
                </div>
                <div className="mt-4 mt-lg-0 mb-4">
                    <RowInfo help={<BioModal />}>
                        <Textarea
                            help={t("This is what players see when they learn about your NPC.")}
                            label={t("Bio")}
                            name="bio"
                            useQuery={useQueryNoInvalidate_}
                            mode="clean"
                            rows="5"
                            maxLength={400}
                        />
                    </RowInfo>
                </div>
                {
                    shouldShowAdvancedSettings
                        ? <AdvancedSettings tabs={settingsTabs} />
                        : null
                }
                <Divider />
                <NpcItemActionButtonWrapper className="justify-content-between d-flex mb-md-2 g-2">
                    <Col size="6" lg="4" className="col--stretch">
                        <ActionDelete />
                    </Col>
                    <Col size="6" lg="4" className="col--stretch justify-content-center">
                        {showAppPreview && (
                            <Button
                                className="btn--flat"
                                rel="noreferrer"
                                outline
                                {...previewBtnProps}
                            >
                                {t('Preview')}
                            </Button>
                        )}
                    </Col>
                    <Col size="6" lg="4" className="col--stretch justify-content-end">
                        <Button data-testid="npc-item__button-save" color="primary" className="btn--flat" onClick={onSave}>
                            {t('Save')}
                        </Button>
                    </Col>
                </NpcItemActionButtonWrapper>
            </MDBCardBody>
        </>
    );
};

export default NpcItemEdit;
